/* Telegram Mini App для водителей — «приложение водителя».
   Открывается внутри логистического бота (кнопка web_app).
   Дизайн: крупно, контрастно, минимум текста. RU/KZ. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/driver-app`;

const T = {
  ru: {
    loading: 'Загрузка…', notTelegram: 'Откройте через Telegram',
    notTelegramHint: 'Это приложение работает внутри бота логистики Айнанан. Откройте его кнопкой «📱 Открыть приложение» в чате с ботом.',
    notBound: 'Вы не привязаны к боту', notBoundHint: 'Попросите диспетчера отправить вам персональную ссылку-привязку.',
    noRoute: 'Маршрута на сегодня нет', noRouteHint: 'Когда диспетчер опубликует маршрут — он появится здесь.',
    refresh: 'Обновить', myRoute: 'Мой маршрут', start: '🚀 НАЧАТЬ МАРШРУТ',
    stops: 'точек', deliveredCnt: 'доставлено', stop: 'Точка', call: '📞 Позвонить',
    map: '🗺 Маршрут', items: 'Состав заказа', delivered: '✅ ДОСТАВЛЕНО',
    problem: '⚠️ Проблема', skip: '⏭ Пропустить', pickedQ: 'Сколько контейнеров ЗАБРАЛИ?',
    leftQ: 'Сколько контейнеров ОСТАВИЛИ?', confirm: '✅ ПОДТВЕРДИТЬ', back: '← Назад',
    next: 'Далее →', whatHappened: 'Что случилось?',
    reasons: { closed: '🚪 Не открывают', no_answer: '📞 Не отвечает', refused: '❌ Отказались', reschedule: '🔄 Перенести' },
    skipConfirm: 'Пропустить эту точку?', yes: 'Да, пропустить', cancel: 'Отмена',
    revisit: '🔄 Вернуться к точке', finish: '🏁 ЗАВЕРШИТЬ МАРШРУТ',
    routeDone: 'Маршрут завершён!', greatJob: 'Отличная работа! Итоги отправлены диспетчеру.',
    summary: (d, t) => `Доставлено ${d} из ${t}`, containers: (l, p) => `Тара: оставил ${l} • забрал ${p}`,
    statuses: { delivered: 'Доставлено', problem: 'Проблема', skipped: 'Пропущена', in_route: 'В пути', queued: 'Ожидает' },
    alreadyDone: 'Точка уже обработана', error: 'Ошибка — попробуйте ещё раз',
    poolNote: 'Pool-распределение тары:', undeliveredLeft: 'Остались точки со статусом «проблема/пропущена»',
  },
  kz: {
    loading: 'Жүктелуде…', notTelegram: 'Telegram арқылы ашыңыз',
    notTelegramHint: 'Бұл қосымша Айнанан логистика ботының ішінде жұмыс істейді. Боттағы «📱 Открыть приложение» батырмасымен ашыңыз.',
    notBound: 'Сіз ботқа байланбағансыз', notBoundHint: 'Диспетчерден жеке байланыстыру сілтемесін сұраңыз.',
    noRoute: 'Бүгінге бағыт жоқ', noRouteHint: 'Диспетчер бағытты жариялағанда — осында пайда болады.',
    refresh: 'Жаңарту', myRoute: 'Менің бағытым', start: '🚀 БАҒЫТТЫ БАСТАУ',
    stops: 'нүкте', deliveredCnt: 'жеткізілді', stop: 'Нүкте', call: '📞 Қоңырау',
    map: '🗺 Бағыт', items: 'Тапсырыс құрамы', delivered: '✅ ЖЕТКІЗІЛДІ',
    problem: '⚠️ Мәселе', skip: '⏭ Өткізу', pickedQ: 'Қанша контейнер АЛДЫҢЫЗ?',
    leftQ: 'Қанша контейнер ҚАЛДЫРДЫҢЫЗ?', confirm: '✅ РАСТАУ', back: '← Артқа',
    next: 'Келесі →', whatHappened: 'Не болды?',
    reasons: { closed: '🚪 Ашпайды', no_answer: '📞 Жауап жоқ', refused: '❌ Бас тартты', reschedule: '🔄 Кейінге' },
    skipConfirm: 'Бұл нүктені өткізіп жіберу?', yes: 'Иә, өткізу', cancel: 'Болдырмау',
    revisit: '🔄 Нүктеге оралу', finish: '🏁 БАҒЫТТЫ АЯҚТАУ',
    routeDone: 'Бағыт аяқталды!', greatJob: 'Жарайсыз! Қорытынды диспетчерге жіберілді.',
    summary: (d, t) => `${t} нүктеден ${d} жеткізілді`, containers: (l, p) => `Ыдыс: қалдырды ${l} • алды ${p}`,
    statuses: { delivered: 'Жеткізілді', problem: 'Мәселе', skipped: 'Өткізілді', in_route: 'Жолда', queued: 'Күтуде' },
    alreadyDone: 'Нүкте өңделіп қойған', error: 'Қате — қайталап көріңіз',
    poolNote: 'Pool ыдыс бөлінісі:', undeliveredLeft: '«Мәселе/өткізілген» нүктелер қалды',
  },
};

const STATUS_ICON = { delivered: '✅', problem: '⚠️', skipped: '⏭', in_route: '▶️', queued: '▫️' };

/* Захватываем hash сразу при загрузке модуля (роутер может его изменить).
   Telegram передаёт initData в #tgWebAppData= — парсим сами, SDK нужен
   только для expand/haptics. */
const INITIAL_HASH = typeof window !== 'undefined' ? window.location.hash : '';
function hashInitData() {
  for (const h of [INITIAL_HASH, typeof window !== 'undefined' ? window.location.hash : '']) {
    try {
      const v = new URLSearchParams((h || '').replace(/^#/, '')).get('tgWebAppData');
      if (v) return v;
    } catch (e) { /* noop */ }
  }
  return '';
}

function mapUrl(stop) {
  if (stop.lat && stop.lng) return `https://yandex.kz/maps/?rtext=~${stop.lat},${stop.lng}`;
  return `https://yandex.kz/maps/?text=${encodeURIComponent('Алматы, ' + stop.address)}`;
}

/* Крупный счётчик тары: − N + и быстрые цифры */
function Counter({ value, onChange }) {
  return (
    <div className="my-5">
      <div className="flex items-center justify-center gap-5">
        <button data-testid="counter-minus" onClick={() => onChange(Math.max(0, value - 1))}
          className="w-20 h-20 rounded-2xl bg-zinc-800 text-white text-5xl font-bold active:scale-95">−</button>
        <div data-testid="counter-value" className="w-28 text-center text-7xl font-black tabular-nums">{value}</div>
        <button data-testid="counter-plus" onClick={() => onChange(Math.min(999, value + 1))}
          className="w-20 h-20 rounded-2xl bg-zinc-800 text-white text-5xl font-bold active:scale-95">+</button>
      </div>
      <div className="grid grid-cols-6 gap-2 mt-4">
        {[0, 1, 2, 3, 4, 5, 6, 8, 10, 12, 15, 20].map(n => (
          <button key={n} onClick={() => onChange(n)} data-testid={`counter-quick-${n}`}
            className={`h-12 rounded-xl text-xl font-bold ${value === n ? 'bg-emerald-600 text-white' : 'bg-zinc-200 text-zinc-800'} active:scale-95`}>
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

function BigButton({ children, onClick, color = 'emerald', testId, disabled }) {
  const colors = {
    emerald: 'bg-emerald-600 text-white', red: 'bg-red-600 text-white',
    zinc: 'bg-zinc-200 text-zinc-900', amber: 'bg-amber-500 text-white',
    dark: 'bg-zinc-900 text-white',
  };
  return (
    <button data-testid={testId} onClick={onClick} disabled={disabled}
      className={`w-full min-h-[64px] rounded-2xl text-2xl font-extrabold tracking-wide ${colors[color]} active:scale-[0.98] disabled:opacity-50 transition-transform`}>
      {children}
    </button>
  );
}

export default function DriverApp() {
  const [lang, setLang] = useState(localStorage.getItem('driver_lang') || 'ru');
  const t = T[lang];
  const [initData, setInitData] = useState('');
  const [phase, setPhase] = useState('loading'); // loading|notg|notbound|noroute|route|deliver|problem|done
  const [route, setRoute] = useState(null);
  const [activeStop, setActiveStop] = useState(null); // order_id открытой карточки
  const [deliverStep, setDeliverStep] = useState('picked'); // picked|left
  const [picked, setPicked] = useState(0);
  const [left, setLeft] = useState(0);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const [skipAsk, setSkipAsk] = useState(false);
  const [finishInfo, setFinishInfo] = useState(null);

  const switchLang = (l) => { setLang(l); localStorage.setItem('driver_lang', l); };
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  // --- Telegram SDK init ---
  useEffect(() => {
    const boot = () => {
      const tg = window.Telegram?.WebApp;
      const data = tg?.initData || hashInitData();
      if (tg) { try { tg.expand(); tg.ready(); } catch (e) { /* noop */ } }
      if (!data) { setPhase('notg'); return; }
      setInitData(data);
    };
    if (window.Telegram?.WebApp) { boot(); return; }
    const s = document.createElement('script');
    s.src = 'https://telegram.org/js/telegram-web-app.js';
    s.onload = boot;
    s.onerror = () => setPhase('notg');
    document.head.appendChild(s);
  }, []);

  const api = useCallback(async (path, opts = {}) => {
    const res = await fetch(`${API}${path}`, {
      ...opts,
      headers: { 'Content-Type': 'application/json', 'X-Telegram-Init-Data': initData, ...(opts.headers || {}) },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const err = new Error(body.detail || 'error');
      err.status = res.status;
      throw err;
    }
    return res.json();
  }, [initData]);

  const loadRoute = useCallback(async () => {
    try {
      const data = await api('/route');
      if (!data.found) { setPhase('noroute'); setRoute(null); return; }
      setRoute(data);
      setPhase('route');
    } catch (e) {
      if (e.status === 403) setPhase('notbound');
      else if (e.status === 401) setPhase('notg');
      else { setPhase('noroute'); showToast(t.error); }
    }
  }, [api, t.error]);

  // Загрузка маршрута строго по факту получения initData.
  // ВАЖНО: не зависит от loadRoute (его identity меняется при смене языка,
  // что вызывало гонку: отложенный loadRoute перекрывал экран «Завершено»).
  useEffect(() => {
    if (initData) loadRoute();
  }, [initData]); // только initData: loadRoute меняется при смене языка (гонка с экраном «Завершено»)

  const stopByid = useMemo(() => {
    const m = {};
    (route?.stops || []).forEach(s => { m[s.order_id] = s; });
    return m;
  }, [route]);

  const current = activeStop ? stopByid[activeStop] : null;
  const allDone = route && route.stops.every(s => ['delivered', 'problem', 'skipped'].includes(s.status));

  const doAction = async (fn) => {
    if (busy) return;
    setBusy(true);
    try { await fn(); } catch (e) {
      showToast(e.status === 409 ? t.alreadyDone : t.error);
      await loadRoute();
      setActiveStop(null);
    } finally { setBusy(false); }
  };

  const openStop = (s) => {
    if (['delivered'].includes(s.status)) return;
    setActiveStop(s.order_id);
    setDeliverStep('picked'); setPicked(0); setLeft(0); setSkipAsk(false);
    setPhase(['problem', 'skipped'].includes(s.status) ? 'revisit' : 'stop');
  };

  const handleStart = () => doAction(async () => {
    const data = await api(`/route/${route.route_id}/start`, { method: 'POST' });
    setRoute(data);
  });

  const handleDeliver = () => doAction(async () => {
    const data = await api(`/route/${route.route_id}/stops/${activeStop}/deliver`, {
      method: 'POST', body: JSON.stringify({ picked, left }),
    });
    if (data.pool_distribution) {
      showToast(`${t.poolNote} ${data.pool_distribution.map(d => `${d.name}: ${d.count}`).join(', ')}`);
    }
    setRoute(data); setActiveStop(null); setPhase('route');
    try { window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success'); } catch (e) { /* noop */ }
  });

  const handleProblem = (code) => doAction(async () => {
    const data = await api(`/route/${route.route_id}/stops/${activeStop}/problem`, {
      method: 'POST', body: JSON.stringify({ reason: code }),
    });
    setRoute(data); setActiveStop(null); setPhase('route');
  });

  const handleSkip = () => doAction(async () => {
    const data = await api(`/route/${route.route_id}/stops/${activeStop}/skip`, { method: 'POST' });
    setRoute(data); setActiveStop(null); setPhase('route'); setSkipAsk(false);
  });

  const handleRevisit = () => doAction(async () => {
    const data = await api(`/route/${route.route_id}/stops/${activeStop}/revisit`, { method: 'POST' });
    setRoute(data); setActiveStop(null); setPhase('route');
  });

  const handleFinish = () => doAction(async () => {
    const info = await api(`/route/${route.route_id}/finish`, { method: 'POST' });
    setFinishInfo(info); setPhase('done');
  });

  // ============ SCREENS ============
  const LangSwitch = (
    <div className="flex gap-1" data-testid="lang-switch">
      {['ru', 'kz'].map(l => (
        <button key={l} onClick={() => switchLang(l)}
          className={`px-3 py-1.5 rounded-lg text-sm font-bold uppercase ${lang === l ? 'bg-zinc-900 text-white' : 'bg-zinc-200 text-zinc-600'}`}>
          {l}
        </button>
      ))}
    </div>
  );

  const Centered = ({ icon, title, hint, retry }) => (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-8 text-center">
      <div className="text-7xl mb-6">{icon}</div>
      <h1 className="text-3xl font-black mb-3">{title}</h1>
      <p className="text-lg text-zinc-500 mb-8 max-w-sm">{hint}</p>
      {retry && <div className="w-full max-w-xs"><BigButton testId="refresh-btn" color="dark" onClick={loadRoute}>{t.refresh}</BigButton></div>}
      <div className="mt-8">{LangSwitch}</div>
    </div>
  );

  if (phase === 'loading') return <Centered icon="🚐" title={t.loading} hint="" />;
  if (phase === 'notg') return <Centered icon="📱" title={t.notTelegram} hint={t.notTelegramHint} />;
  if (phase === 'notbound') return <Centered icon="🔗" title={t.notBound} hint={t.notBoundHint} retry />;
  if (phase === 'noroute') return <Centered icon="😴" title={t.noRoute} hint={t.noRouteHint} retry />;

  if (phase === 'done') {
    return (
      <div className="min-h-screen bg-emerald-600 flex flex-col items-center justify-center p-8 text-center text-white">
        <div className="text-8xl mb-6">🏁</div>
        <h1 className="text-4xl font-black mb-3" data-testid="route-done-title">{t.routeDone}</h1>
        {finishInfo && <p className="text-2xl font-bold mb-2">{t.summary(finishInfo.delivered, finishInfo.total)}</p>}
        {finishInfo && <p className="text-lg opacity-90">{t.containers(finishInfo.left, finishInfo.picked)}</p>}
        <p className="text-lg mt-6 opacity-90">{t.greatJob}</p>
      </div>
    );
  }

  // -------- Карточка точки / доставка / проблема --------
  if (current && (phase === 'stop' || phase === 'deliver' || phase === 'problem' || phase === 'revisit')) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <div className="bg-zinc-900 text-white px-5 py-4 flex items-center justify-between">
          <button data-testid="stop-back-btn" onClick={() => { setActiveStop(null); setPhase('route'); }}
            className="text-xl font-bold">{t.back}</button>
          <div className="text-lg font-bold">{t.stop} {current.seq}/{route.total}</div>
        </div>

        <div className="flex-1 p-5 overflow-y-auto">
          <h1 className="text-3xl font-black leading-tight" data-testid="stop-client">{current.client}</h1>
          {current.time && <div className="inline-block mt-2 px-3 py-1.5 bg-amber-100 text-amber-900 rounded-lg text-xl font-bold">🕐 {current.time}</div>}
          <p className="text-xl text-zinc-600 mt-3">{current.address}</p>

          {phase === 'stop' && (
            <>
              <div className="grid grid-cols-2 gap-3 mt-5">
                {current.phone && (
                  <a data-testid="stop-call-btn" href={`tel:${current.phone}`}
                    className="min-h-[56px] rounded-2xl bg-zinc-200 text-zinc-900 text-xl font-bold flex items-center justify-center">{t.call}</a>)}
                <a data-testid="stop-map-btn" href={mapUrl(current)} target="_blank" rel="noreferrer"
                  className="min-h-[56px] rounded-2xl bg-zinc-200 text-zinc-900 text-xl font-bold flex items-center justify-center">{t.map}</a>
              </div>
              {current.items.length > 0 && (
                <div className="mt-5 bg-zinc-50 rounded-2xl p-4">
                  <div className="text-sm font-bold text-zinc-400 uppercase mb-2">{t.items}</div>
                  {current.items.map((it, i) => (
                    <div key={i} className="flex justify-between text-lg py-0.5">
                      <span className="pr-3">{it.name}</span><span className="font-bold whitespace-nowrap">× {it.qty}</span>
                    </div>))}
                </div>)}
              <div className="mt-6 space-y-3">
                <BigButton testId="deliver-btn" onClick={() => setPhase('deliver')}>{t.delivered}</BigButton>
                <div className="grid grid-cols-2 gap-3">
                  <BigButton testId="problem-btn" color="amber" onClick={() => setPhase('problem')}>{t.problem}</BigButton>
                  <BigButton testId="skip-btn" color="zinc" onClick={() => setSkipAsk(true)}>{t.skip}</BigButton>
                </div>
              </div>
              {skipAsk && (
                <div className="fixed inset-0 bg-black/60 flex items-end z-50" onClick={() => setSkipAsk(false)}>
                  <div className="bg-white w-full rounded-t-3xl p-6 space-y-3" onClick={e => e.stopPropagation()}>
                    <h2 className="text-2xl font-black text-center mb-4">{t.skipConfirm}</h2>
                    <BigButton testId="skip-confirm-btn" color="red" onClick={handleSkip} disabled={busy}>{t.yes}</BigButton>
                    <BigButton testId="skip-cancel-btn" color="zinc" onClick={() => setSkipAsk(false)}>{t.cancel}</BigButton>
                  </div>
                </div>)}
            </>
          )}

          {phase === 'deliver' && (
            <div className="mt-6">
              <h2 className="text-2xl font-black text-center" data-testid="deliver-question">
                {deliverStep === 'picked' ? t.pickedQ : t.leftQ}
              </h2>
              {deliverStep === 'picked'
                ? <Counter value={picked} onChange={setPicked} />
                : <Counter value={left} onChange={setLeft} />}
              <div className="space-y-3 mt-2">
                {deliverStep === 'picked'
                  ? <BigButton testId="deliver-next-btn" onClick={() => setDeliverStep('left')}>{t.next}</BigButton>
                  : <BigButton testId="deliver-confirm-btn" onClick={handleDeliver} disabled={busy}>{t.confirm}</BigButton>}
                <BigButton testId="deliver-back-btn" color="zinc"
                  onClick={() => deliverStep === 'left' ? setDeliverStep('picked') : setPhase('stop')}>{t.back}</BigButton>
              </div>
            </div>
          )}

          {phase === 'problem' && (
            <div className="mt-6 space-y-3">
              <h2 className="text-2xl font-black text-center mb-4">{t.whatHappened}</h2>
              {Object.entries(t.reasons).map(([code, label]) => (
                <BigButton key={code} testId={`problem-reason-${code}`} color="zinc"
                  onClick={() => handleProblem(code)} disabled={busy}>{label}</BigButton>))}
              <BigButton testId="problem-back-btn" color="dark" onClick={() => setPhase('stop')}>{t.back}</BigButton>
            </div>
          )}

          {phase === 'revisit' && (
            <div className="mt-6 space-y-3">
              <div className="bg-amber-50 text-amber-900 rounded-2xl p-4 text-lg font-bold text-center">
                {STATUS_ICON[current.status]} {t.statuses[current.status]}
                {current.problem_note ? ` — ${t.reasons[current.problem_note] || current.problem_note}` : ''}
              </div>
              <BigButton testId="revisit-btn" onClick={handleRevisit} disabled={busy}>{t.revisit}</BigButton>
            </div>
          )}
        </div>
      </div>
    );
  }

  // -------- Главный экран: прогресс + список точек --------
  const pct = route.total ? Math.round((route.done / route.total) * 100) : 0;
  return (
    <div className="min-h-screen bg-zinc-100 pb-32">
      <div className="bg-zinc-900 text-white px-5 pt-5 pb-6 rounded-b-3xl">
        <div className="flex items-center justify-between mb-1">
          <h1 className="text-2xl font-black" data-testid="route-title">🚐 {route.vehicle_name || t.myRoute}</h1>
          {LangSwitch}
        </div>
        <div className="text-zinc-400 text-lg">{route.date} • {route.total} {t.stops}</div>
        <div className="mt-4">
          <div className="flex justify-between text-lg font-bold mb-1.5">
            <span data-testid="route-progress">{route.done}/{route.total} {t.deliveredCnt}</span><span>{pct}%</span>
          </div>
          <div className="h-4 bg-zinc-700 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {route.stops.map((s) => {
          const isCurrent = s.seq - 1 === route.current_index && !['delivered', 'problem', 'skipped'].includes(s.status);
          return (
            <button key={`${s.order_id}-${s.seq}`} data-testid={`stop-item-${s.seq}`} onClick={() => openStop(s)}
              className={`w-full text-left rounded-2xl p-4 flex items-center gap-4 transition-transform active:scale-[0.99]
                ${isCurrent ? 'bg-white ring-4 ring-emerald-500 shadow-lg' : s.status === 'delivered' ? 'bg-emerald-50' : 'bg-white'}`}>
              <div className="text-3xl w-10 text-center shrink-0">{STATUS_ICON[s.status] || '▫️'}</div>
              <div className="min-w-0 flex-1">
                <div className={`text-xl font-extrabold truncate ${s.status === 'delivered' ? 'text-zinc-400 line-through' : ''}`}>
                  {s.seq}. {s.client}
                </div>
                <div className="text-zinc-500 truncate">{s.time && <b className="text-amber-700">{s.time} • </b>}{s.address}</div>
              </div>
              {isCurrent && <div className="text-emerald-600 text-3xl shrink-0">→</div>}
            </button>
          );
        })}
      </div>

      <div className="fixed bottom-0 inset-x-0 p-4 bg-gradient-to-t from-zinc-100 via-zinc-100 to-transparent">
        {!route.started && <BigButton testId="start-route-btn" onClick={handleStart} disabled={busy}>{t.start}</BigButton>}
        {route.started && allDone && <BigButton testId="finish-route-btn" color="dark" onClick={handleFinish} disabled={busy}>{t.finish}</BigButton>}
      </div>

      {toast && (
        <div data-testid="toast" className="fixed top-4 inset-x-4 bg-zinc-900 text-white rounded-2xl px-5 py-4 text-lg font-bold text-center z-50">
          {toast}
        </div>)}
    </div>
  );
}
